// ============================================================
// หน้า Remittance — การนำส่งเงินค่าใช้จ่าย
// ------------------------------------------------------------
// แสดงเอกสารนำส่งธนาคาร (RW) แต่ละใบ พร้อมยอดแยกตามประเภทค่าใช้จ่าย
// และออกรายงานการจัดเก็บ/นำส่งได้หลายแบบ (PDF และ Excel)
//
// สิ่งที่ทำได้
//   - ค้นหาตามเดือน / คำค้น
//   - บันทึกวันที่นำเงินฝากธนาคารของแต่ละเอกสาร
//   - พิมพ์รายงาน 4 แบบ + พิมพ์รายงานรายเอกสาร
//
// ★ ยอดของแต่ละเอกสารไม่ได้มาจาก backend ตรง ๆ แต่คำนวณใหม่ฝั่งหน้าจอ
//   จากใบเสร็จย่อย (detail_rr) ดูที่ processBillData()
// ============================================================

import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import { DownloadTableExcel } from "react-export-table-to-excel";

import "../../shared/config/global.js";
import "../../assets/css/bootstrap.css";
import Navbar from "../../shared/layout/Navbar";
import { tokens, success, alert, toFixed, GET, POST, PUT, format_date, DatePicker, getStorage, roundToTwo, total, loading, permission, Paginate, ActionIcon, ICON } from "../../shared/index.js";
import { report_remittance_byid } from "../../documents/builders/ReportRemittanceByID.js";
import { report_remittance_1 } from "../../documents/builders/ReportRemittance01.js";
import { report_remittance_2 } from "../../documents/builders/ReportRemittance02.js";
import { report_remittance_3 } from "../../documents/builders/ReportRemittance03.js";
import { report_remittance_4 } from "../../documents/builders/ReportRemittance04.js";

const perm = permission("remittance");

// ประเภทบัญชีที่นำฝาก (index ตรงกับ payment_type)
const PAYMENT_LABELS = ["-", "บัญชีค่าน้ำ และส่วนกลาง", "โอนเงินเข้าบัญชีนิติ", "เงินกองทุน"];

// สถานะใบเสร็จ 2 = ยกเลิกแล้ว
const BILL_CANCELED = 2;

// เกิน 2 วันแล้วยังไม่บันทึกวันนำฝาก ถือว่าผิดปกติ (2 วัน = 172,800,000 มิลลิวินาที)
const DEPOSIT_OVERDUE_MS = 2 * 24 * 60 * 60 * 1000;

// ค่าที่ต้องรวมของแต่ละเอกสารนำส่ง
const SUM_KEYS = ["add_debt", "cable_cost", "change_satang", "common_expenses", "equipment_maintenance_fee", "late_payment", "meter_connect_cost", "other_income", "reduce_debt", "total_amount", "total_final", "waste_cost", "water_bill", "fire_cost"];

// ช่องเงินบางช่อง backend ส่งมาเป็นข้อความมี comma ต้องแปลงเป็นตัวเลขก่อนคิด
const parseCost = (value) => Number(String(toFixed(value)).replace(/,/g, ""));

// ยอดรวมของเอกสารนำส่ง 1 ใบ
// NOTE add_debt/reduce_debt ผ่าน processBillData มาแล้ว รวม add_on_* ไว้ในตัวเอง — บวก add_on_* ซ้ำอีกจะได้ 2 เท่า
const depositTotal = (item) =>
  item.water_bill +
  item.equipment_maintenance_fee +
  item.common_expenses +
  item.waste_cost +
  item.cable_cost +
  item.other_income +
  item.fire_cost +
  item.meter_connect_cost +
  item.late_payment +
  item.change_satang +
  ((item.add_debt || 0) - (item.reduce_debt || 0));

// หัวคอลัมน์ตารางหลัก
const TABLE_HEADERS = [
  "เลขที่ใบเสร็จรับเงิน",
  "ปิดงานประจำวันที่",
  "บัญชีนำฝาก",
  "จำนวนเงินฝาก",
  "วันที่นำเงินฝากธนาคาร",
  "เลขที่ใบเสร็จใบแรก",
  "เลขที่ใบเสร็จใบสุดท้าย",
  "ค่าน้ำประปา",
  "ค่ารักษาอุปกรณ์น้ำ",
  "ค่าส่วนกลาง",
  "ค่าขยะ",
  "ค่าเคเบิล",
  "รายรับอื่นๆ",
  "ค่าเบี้ยประกัน",
  "เพิ่ม/ลดหนี้",
  "ค่าต่อกลับมิเตอร์",
  "ค่าปรับชำระล่าช้า",
  "ปรับยอดเงิน",
  "รวมเป็นเงิน",
  "พิมพ์รายงาน",
];

// หัวคอลัมน์ตารางที่ซ่อนไว้สำหรับส่งออก Excel
const EXCEL_HEADERS = [
  "ลำดับ",
  "เลขที่ใบเสร็จรับเงิน",
  "วันที่ใบเสร็จรับเงิน",
  "บ้านเลขที่",
  "ชื่อ-นามสกุล",
  "ชำระ",
  "วันที่โอน",
  "ค่าน้ำประปา",
  "ค่ารักษาอุปกรณ์น้ำ",
  "ค่าส่วนกลาง",
  "ค่าขยะ",
  "รายรับอื่นๆ",
  "เพิ่ม/ ลดหนี้",
  "ค่าต่อกลับมิเตอร์",
  "ค่าปรับชำระล่าช้า",
  "ปรับเศษ",
  "รวมทั้งสิ้น",
];

export default class Remittance extends Component {
  constructor(props) {
    super(props);
    const today = new Date();
    this.state = {
      // popup
      modal_update: false,
      modal_report: false,
      modal_report2: false,
      // ตัวกรอง
      filter_data_search: "",
      filter_type: "",
      page: 1,
      select_month: format_date(today, "yyyy-mm", "en"),
      start_date: format_date(new Date().setDate(1), "yyyy-mm-dd", "en"),
      end_date: format_date(new Date(new Date().setMonth(today.getMonth() + 1)).setDate(0), "yyyy-mm-dd", "en"),
      // ข้อมูล
      data: null, // เอกสารนำส่งของหน้าปัจจุบัน
      data_day: [], // เอกสารนำส่งทั้งหมดในช่วงที่เลือก (ใช้กับรายงานประจำวัน)
      data_table: null, // แถวของตารางส่งออก Excel
      array_rw_number: [], // เลขที่เอกสารที่จะเอาไปออกรายงาน
      // ฟอร์มบันทึกวันนำฝาก
      rw_number: "",
      bill_group_id: "",
      deposit_date: today,
    };
    this.tableRef = React.createRef();
  }

  async componentDidMount() {
    loading(false);
    if (!perm.view) return;
    await Promise.all([this.GetAll(), this.GetReportDay()]);
  }

  // ---------- คำนวณยอดของเอกสารนำส่ง ----------

  // รวมยอดของใบเสร็จ 1 ใบเข้า sums
  //   ใบที่มีรายการรับชำระย่อย (transaction_payments) → รวมจากรายการย่อย
  //   ใบที่ไม่มี → รวมจากค่าบนใบเสร็จเอง
  // นับเฉพาะช่องที่ถูกเก็บเงินจริง (s_* = true)
  addReceiptToSums = (sums, item, calculateFinal) => {
    if (item.transaction_payments?.length > 0) {
      sums.change_satang += item.change_satang || 0;
      // NOTE add_on_* อยู่ที่ระดับ detail_rr ไม่ได้อยู่ใน transaction_payments — บวกครั้งเดียวต่อ 1 ใบ
      // (เดิมอ่าน tp.add_on_* ซึ่งไม่มีฟิลด์นี้ ค่าเลยหายไปทั้งก้อน)
      sums.add_debt += item.add_on_add_debt || 0;
      sums.reduce_debt += item.add_on_reduce_debt || 0;

      item.transaction_payments.forEach((tp) => {
        sums.add_debt += tp.s_add_debt ? tp.add_debt : 0;
        sums.cable_cost += tp.s_cable_cost ? parseCost(tp.cable_cost) : 0;
        sums.common_expenses += tp.s_common_expenses ? parseCost(tp.common_expenses) : 0;
        sums.equipment_maintenance_fee += tp.s_equipment_maintenance_fee ? tp.equipment_maintenance_fee : 0;
        sums.late_payment += tp.s_late_payment ? parseCost(tp.late_payment) : 0;
        sums.meter_connect_cost += tp.s_meter_connect_cost ? tp.meter_connect_cost : 0;
        sums.other_income += (tp.s_other_income ? tp.other_income : 0) + (tp.detail_rr_other?.reduce((s, o) => s + (o.cost || 0), 0) || 0);
        sums.reduce_debt += tp.s_reduce_debt ? tp.reduce_debt : 0;
        sums.total_amount += tp.total_amount || 0;
        sums.waste_cost += tp.s_waste_cost ? tp.waste_cost : 0;
        sums.water_bill += tp.s_water_bill ? tp.water_bill : 0;
        sums.fire_cost += tp.s_fire_cost ? parseCost(tp.fire_cost) : 0;
        if (!calculateFinal) sums.total_final += tp.total_final || 0;
      });
      return;
    }

    // ใบที่ไม่มีรายการย่อย
    sums.add_debt += (item.s_add_debt ? item.add_debt : 0) + (item.add_on_add_debt || 0);
    sums.cable_cost += item.s_cable_cost ? parseCost(item.cable_cost) : 0;
    sums.change_satang += item.change_satang || 0;
    sums.common_expenses += item.s_common_expenses ? parseCost(item.common_expenses) : 0;
    sums.equipment_maintenance_fee += item.s_equipment_maintenance_fee ? item.equipment_maintenance_fee : 0;
    sums.late_payment += item.s_late_payment ? parseCost(item.late_payment) : 0;
    sums.meter_connect_cost += item.s_meter_connect_cost ? item.meter_connect_cost : 0;
    sums.other_income += (item.s_other_income ? item.other_income : 0) + (item.detail_rr_other?.reduce((s, o) => s + (o.cost || 0), 0) || 0);
    sums.reduce_debt += (item.s_reduce_debt ? item.reduce_debt : 0) + (item.add_on_reduce_debt || 0);
    sums.total_amount += item.total_amount || 0;
    sums.waste_cost += item.s_waste_cost ? item.waste_cost : 0;
    sums.water_bill += item.s_water_bill ? item.water_bill : 0;
    sums.fire_cost += item.s_fire_cost ? parseCost(item.fire_cost) : 0;
    if (!calculateFinal) sums.total_final += item.total_final || 0;
  };

  // คิดยอดรวมของทุกเอกสารนำส่งจากใบเสร็จย่อย (ข้ามใบที่ถูกยกเลิก)
  //   calculateFinal = true  → total_final คิดใหม่จากผลรวมทุกช่อง
  //   calculateFinal = false → total_final ใช้ค่าที่ backend ส่งมา
  processBillData = (data, calculateFinal = false) => {
    data.forEach((d) => {
      const sums = SUM_KEYS.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
      d.detail_rr?.filter((i) => i.status_bill !== BILL_CANCELED).forEach((item) => this.addReceiptToSums(sums, item, calculateFinal));

      if (calculateFinal) {
        sums.total_final =
          sums.water_bill + sums.equipment_maintenance_fee + sums.common_expenses + sums.fire_cost + sums.waste_cost + sums.cable_cost + sums.other_income + sums.add_debt + sums.meter_connect_cost + sums.late_payment + sums.change_satang - sums.reduce_debt;
      }
      Object.assign(d, sums);
    });
    return data;
  };

  // ---------- โหลดข้อมูล ----------

  // NOTE เดิมมี CheckTotalAmount เรียกทุกครั้งที่เปิดหน้า — เอายอดหน้าบิล "ปัจจุบัน" ของทุกบิลในใบเสร็จ
  //      ไปเขียนทับยอดรายช่อง + total_amount ของใบเสร็จ (ค่าปรับของบิลถูกคิดใหม่ทีหลัง / บิลที่ไม่ได้จ่ายในใบนั้นก็ถูกรวม)
  //      แต่ total_final (เงินที่รับจริง) ไม่เปลี่ยน -> ยอดบนใบเสร็จไม่ตรงกับเงินที่รับ ถอดออกแล้ว
  //      ใบเก่าที่เพี้ยนไปแล้วซ่อมด้วย backend: python manage.py fix_receipt_totals

  // เงื่อนไขการค้นหา (ใช้ร่วมกันหลายที่)
  searchPayload = () => ({ data_search: this.state.filter_data_search, start_date: this.state.start_date, end_date: this.state.end_date });

  GetAll = async () => {
    loading(true);
    try {
      const res = await GET(tokens, `v1/TH/bill-water-group/send/filter-search/page/${this.state.page}/${getStorage("project_id")}`, this.searchPayload());
      if (res?.status) this.setState({ data: { ...res.data, data: this.processBillData(res.data.data, true) } });
    } finally {
      loading(false);
    }
  };

  // เอกสารนำส่งทั้งหมดในช่วงที่เลือก (ใช้กับรายงานประจำวัน)
  GetReportDay = async () => {
    loading(true);
    try {
      const res = await GET(tokens, `v1/TH/bill-water-group/send/filter-search/page/0/${getStorage("project_id")}`, this.searchPayload());
      if (res?.status) this.setState({ data_day: this.processBillData(res.data.data, true) });
    } finally {
      loading(false);
    }
  };

  // รวบเลขที่เอกสารทั้งหมดในช่วงที่เลือก (ใช้ก่อนออกรายงานรวม)
  GetNopageAll = async () => {
    loading(true);
    try {
      const res = await GET(tokens, `v1/TH/bill-water-group/send/filter-search/page/0/${getStorage("project_id")}`, this.searchPayload());
      if (res?.status) this.setState({ array_rw_number: [...new Set(res.data.data.map((d) => d.rw_number).filter(Boolean))] });
    } finally {
      loading(false);
    }
  };

  // บันทึกวันที่นำเงินฝากธนาคาร
  Update = async () => {
    loading(true);
    try {
      const res = await PUT(tokens, "v1/TH/bill-water-group/send/update", {
        bill_group_id: this.state.bill_group_id,
        deposit_date: this.state.deposit_date ? new Date(this.state.deposit_date).toISOString().split("T")[0] : "",
      });
      if (res?.status) {
        this.clear_form();
        success();
        this.GetAll();
      }
    } finally {
      loading(false);
    }
  };

  clear_form = () => this.setState({ modal_update: false, search: "", page: 1, rw_number: "", bill_group_id: "", deposit_date: new Date(), select_month: "", start_date: "", end_date: "" });

  // ---------- ออกรายงาน ----------

  // ยอดที่ "เก็บเงินแล้ว" ของช่องหนึ่ง จากรายการบิลย่อย
  paidOf = (rows, flagKey, amountKey) => total(rows.filter((e) => e[flagKey]), amountKey);

  // คิดยอดของใบเสร็จ 1 ใบใหม่จากบิลย่อย (ใช้ตอนออกรายงาน)
  recalcReceipt = (item) => {
    const rows = item.transaction_payments.length > 0 ? item.transaction_payments : item.record_water_list;
    const water_bill = this.paidOf(rows, "s_water_bill", "water_bill");
    const waste_cost = this.paidOf(rows, "s_waste_cost", "waste_cost");
    const other_income = this.paidOf(rows, "s_other_income", "other_income");
    const meter_connect_cost = this.paidOf(rows, "s_meter_connect_cost", "meter_connect_cost");
    const late_payment = this.paidOf(rows, "s_late_payment", "late_payment");
    const fire_cost = this.paidOf(rows, "s_fire_cost", "fire_cost");
    const equipment_maintenance_fee = this.paidOf(rows, "s_equipment_maintenance_fee", "equipment_maintenance_fee");
    const common_expenses = this.paidOf(rows, "s_common_expenses", "common_expenses");
    const cable_cost = this.paidOf(rows, "s_cable_cost", "cable_cost");
    const add_debt = this.paidOf(rows, "s_add_debt", "add_debt");
    const reduce_debt = this.paidOf(rows, "s_reduce_debt", "reduce_debt");

    const total_amount =
      water_bill + waste_cost + other_income + meter_connect_cost + late_payment + fire_cost + equipment_maintenance_fee + common_expenses + cable_cost + (add_debt + item.add_on_add_debt) - (reduce_debt + item.add_on_reduce_debt);

    return { ...item, water_bill, waste_cost, other_income, meter_connect_cost, late_payment, fire_cost, equipment_maintenance_fee, common_expenses, cable_cost, add_debt, reduce_debt, total_amount, total_final: total_amount };
  };

  // ออกรายงานจากเลขที่เอกสารใน array_rw_number
  //   type: "allpdf" | "allpdf2" | "allexcel" | "allexcel2" | "2" | อื่น ๆ (รายงานรายเอกสาร)
  GetById = async (type, item) => {
    loading(true);
    try {
      const { array_rw_number, start_date, end_date, filter_type } = this.state;
      const res = await POST(tokens, `v1/TH/bill-water-group/send/by-rw-number/${getStorage("project_id")}`, { rw_number: array_rw_number });
      if (!res?.status) {
        alert("warning", "แจ้งเตือน", "ไม่มีใบแจ้งหนี้");
        this.clear_form();
        return;
      }

      let array = res.data.data.map(this.recalcReceipt);

      // รายงานรวมทั้งช่วง ต้องกรองเฉพาะใบเสร็จที่อยู่ในช่วงวันที่ที่เลือก
      if (start_date && ["allpdf", "allpdf2", "allexcel", "allexcel2", "2"].includes(type)) {
        const startMs = format_date(start_date, "date").getTime();
        const endMs = format_date(end_date, "date").getTime();
        array = array.filter((e) => {
          const recordMs = format_date(e.record_date, "date").getTime();
          return recordMs >= startMs && recordMs <= endMs;
        });
      }

      const paymentType = Number(filter_type);
      // เฉพาะใบที่ชำระด้วยวิธีที่เลือก
      const byPaymentType = (arr) => arr.filter((e) => e.payment_type === paymentType);
      // เฉพาะใบที่โอนเงินข้ามเดือนกับวันที่ออกใบเสร็จ (เงินโอนที่นำส่งเดือนถัดไป)
      const crossMonth = (arr) => arr.filter((e) => new Date(new Date(e.transfer_date).setDate(1)) < new Date(new Date(e.record_date).setDate(1)));
      const reportDate = start_date || new Date();

      if (type === "allpdf") return report_remittance_3(byPaymentType(array), reportDate, paymentType);
      if (type === "allpdf2") return report_remittance_4(crossMonth(byPaymentType(array)), reportDate, paymentType);
      if (type === "2") return report_remittance_2(array, reportDate);
      if (type.startsWith("allexcel")) return this.buildExcelRows(type === "allexcel2" ? crossMonth(byPaymentType(array)) : byPaymentType(array));

      report_remittance_byid(array, item);
    } finally {
      loading(false);
    }
  };

  // สร้างแถวของตารางที่ซ่อนไว้ แล้วสั่งกดปุ่มส่งออก Excel ให้อัตโนมัติ
  // ⚠️ ปุ่มส่งออกมี id="export_excel" ซ้ำกัน 2 ที่ (popup รายงาน 2 ตัว)
  //    getElementById จะเจอตัวแรกเสมอ — ถ้าไฟล์ที่ได้ชื่อไม่ตรงกับ popup ที่เปิด สาเหตุคือจุดนี้
  buildExcelRows = (rows) => {
    const sums = { water: 0, eq: 0, common: 0, waste: 0, other: 0, debt: 0, meter: 0, late: 0, satang: 0, total: 0 };

    const data = rows.map((item, i) => {
      // ใบที่ถูกยกเลิก ยอดทุกช่องเป็น 0
      if (item.status_bill === BILL_CANCELED) {
        // add_on_* ต้องล้างด้วย ไม่งั้นใบที่ยกเลิกยังโผล่ในคอลัมน์เพิ่ม/ลดหนี้และยอดรวม
        Object.assign(item, { water_bill: 0, equipment_maintenance_fee: 0, common_expenses: 0, waste_cost: 0, other_income: 0, add_debt: 0, reduce_debt: 0, meter_connect_cost: 0, late_payment: 0, change_satang: 0, total_final: 0, add_on_add_debt: 0, add_on_reduce_debt: 0 });
      }
      item.count_bill = item.count_bill || 1;
      // ใบรายรับอื่นๆ ให้ใช้ราคาของรายการนั้นแทน (ใบที่ยกเลิกต้องคงเป็น 0 ห้ามดึงกลับ)
      if (item.status_bill !== BILL_CANCELED) item.other_income = item.record_other_list?.[0]?.cost ?? item.other_income;

      // คอลัมน์ "รายรับอื่นๆ" ในรายงานนี้รวมค่าเบี้ยประกันและค่าเคเบิลด้วย
      const otherTotal = item.other_income + item.fire_cost + item.cable_cost;
      const debtTotal = item.add_debt + item.add_on_add_debt - (item.reduce_debt + item.add_on_reduce_debt);

      sums.water += item.water_bill;
      sums.eq += item.equipment_maintenance_fee;
      sums.common += item.common_expenses;
      sums.waste += item.waste_cost;
      sums.other += otherTotal;
      sums.debt += debtTotal;
      sums.meter += item.meter_connect_cost;
      sums.late += item.late_payment;
      sums.satang += item.change_satang;
      sums.total += item.total_final;

      return (
        <tr key={i}>
          <td>{i + 1}</td>
          <td>{item.iw_number}</td>
          <td>{format_date(item.record_date, "dd/mm/yyyy")}</td>
          <td>{item.address_number || item.address || "-"}</td>
          <td>{item.full_name}</td>
          <td>{item.status_bill === BILL_CANCELED ? "ยกเลิก" : `ชำระ ${item.count_bill} รายการ ${item.rw_number}`}</td>
          <td>{item.status_bill !== BILL_CANCELED && item.payment_type === 2 ? format_date(item.transfer_date, "dd/mm/yyyy") : ""}</td>
          <td>{toFixed(item.water_bill)}</td>
          <td>{toFixed(item.equipment_maintenance_fee)}</td>
          <td>{toFixed(item.common_expenses)}</td>
          <td>{toFixed(item.waste_cost)}</td>
          <td>{toFixed(otherTotal)}</td>
          <td>{toFixed(debtTotal)}</td>
          <td>{toFixed(item.meter_connect_cost)}</td>
          <td>{toFixed(item.late_payment)}</td>
          <td>{toFixed(item.change_satang)}</td>
          <td>{toFixed(item.total_final)}</td>
        </tr>
      );
    });

    data.push(
      <tr key="totals">
        <td colSpan={6}></td>
        <td>รวมทั้งสิ้น</td>
        <td>{toFixed(sums.water)}</td>
        <td>{toFixed(sums.eq)}</td>
        <td>{toFixed(sums.common)}</td>
        <td>{toFixed(sums.waste)}</td>
        <td>{toFixed(sums.other)}</td>
        <td>{toFixed(sums.debt)}</td>
        <td>{toFixed(sums.meter)}</td>
        <td>{toFixed(sums.late)}</td>
        <td>{toFixed(sums.satang)}</td>
        <td>{toFixed(sums.total)}</td>
      </tr>
    );

    // รอ 3 วินาทีให้ตารางซ่อน render เสร็จก่อน แล้วค่อยสั่งดาวน์โหลด
    this.setState({ data_table: data }, () => setTimeout(() => document.getElementById("export_excel")?.click(), 3000));
  };

  // รายงานจัดเก็บประจำเดือน — คิดยอดจากใบเสร็จโดยตรง (คนละชุดข้อมูลกับ GetById)
  GetById2 = async () => {
    loading(true);
    try {
      const { start_date, end_date } = this.state;
      const res = await GET(tokens, `v1/TH/report/receipt/filter-search/${getStorage("project_id")}`, { address_number: "", start_date, end_date });
      if (!res?.status) return;

      const array = res.data.data.filter((d) => d.record_water_list?.length > 0 && d.rw_number).map((d) => this.applyReceiptRows(d));
      report_remittance_2(array, start_date || new Date());
    } finally {
      loading(false);
    }
  };

  // กระจายยอดของใบเสร็จลงบิลย่อยแต่ละใบ แล้วคิดยอดรวมของแต่ละบิล
  // ช่องที่ใบเสร็จไม่ได้เก็บ (s_* = false) จะถูกล้างเป็น 0 ทั้งบนใบเสร็จและบิลย่อย
  // add_on_* (เพิ่ม/ลดหนี้ตอนรับชำระ) นับเฉพาะบิลใบแรกเท่านั้น
  applyReceiptRows = (d) => {
    // [ฟิลด์สถานะบนใบเสร็จ, ฟิลด์ยอดบนใบเสร็จ, ฟิลด์ยอดบนบิลย่อย]
    const fields = [
      ["s_cable_cost", "cable_cost", "cable_cost"],
      ["s_common_expenses", "common_expenses", "common_expenses"],
      ["s_equipment_maintenance_fee", "equipment_maintenance_fee", "equipment_maintenance_fee"],
      ["s_fire_cost", "fire_cost", "fire_cost"],
      ["s_late_payment", "late_payment", "late_payment"],
      ["s_meter_connect_cost", "meter_connect_cost", "meter_connect_cost"],
      ["s_other_income", "other_income", "other_income"],
      ["s_waste_cost", "waste_cost", "waste_cost"],
      ["s_water_bill", "water_bill", "water_bill"],
    ];

    ["late_payment", "common_expenses", "cable_cost", "fire_cost"].forEach((k) => (d[k] = parseCost(d[k])));

    // ใช้รายการรับชำระย่อยถ้ามี ไม่มีก็ใช้บิลค่าน้ำที่ผูกกับใบเสร็จ
    const rows = d.transaction_payments?.length > 0 ? d.transaction_payments : d.record_water_list;

    rows.forEach((rw, i) => {
      ["late_payment", "common_expenses", "cable_cost", "fire_cost"].forEach((k) => (rw[k] = parseCost(rw[k])));

      let sum = 0;
      const isFirst = i === 0;

      fields.forEach(([flagKey, receiptKey, rowKey]) => {
        if (!d[flagKey]) {
          d[receiptKey] = 0;
          rw[rowKey] = 0;
        } else {
          sum += rw[rowKey] || 0;
        }
      });

      // เพิ่ม/ลดหนี้: ถ้าใบเสร็จไม่ได้เก็บช่องนี้ ให้ใช้ยอดที่ปรับตอนรับชำระ (เฉพาะบิลใบแรก)
      if (!d.s_add_debt) {
        d.add_debt = rw.add_debt = isFirst ? d.add_on_add_debt || 0 : 0;
      } else {
        sum += rw.add_debt || 0;
        d.add_debt = rw.add_debt;
      }
      if (!d.s_reduce_debt) {
        d.reduce_debt = rw.reduce_debt = isFirst ? d.add_on_reduce_debt || 0 : 0;
      } else {
        sum -= rw.reduce_debt || 0;
        d.reduce_debt = rw.reduce_debt;
      }

      sum += isFirst ? (d.add_on_add_debt || 0) - (d.add_on_reduce_debt || 0) : 0;
      d.total_amount = rw.total_amount = roundToTwo(sum);
    });

    d.add_debt += total(rows, "add_debt");
    d.reduce_debt += total(rows, "reduce_debt");
    return d;
  };

  // ปุ่ม PDF/EXCEL ใน popup รายงาน — รวบเลขที่เอกสารก่อนแล้วค่อยออกรายงาน
  ExportReport = async (type) => {
    loading(true);
    const { start_date, end_date } = this.state;
    try {
      const res = await GET(tokens, `v1/TH/bill-water-group/send/filter-search/page/0/${getStorage("project_id")}`, { data_search: "", start_date, end_date });
      this.setState({ array_rw_number: res?.status ? [...new Set(res.data.data.map((d) => d.rw_number).filter(Boolean))] : [] }, () => this.GetById(`all${type}`, null));
    } catch {
      loading(false);
    }
  };

  // ---------- ตัวช่วยฟอร์ม ----------

  // เลือกเดือน → ตั้งช่วงวันที่เป็นวันแรกถึงวันสุดท้ายของเดือนนั้น
  onSelectMonth = (value, extraState = {}) => {
    if (!value) return this.setState({ start_date: "", end_date: "", ...extraState });
    const first = new Date(`${value}-01`);
    const nextMonth = new Date(new Date(`${value}-01`).setMonth(first.getMonth() + 1));
    this.setState({
      start_date: format_date(first, "yyyy-mm-dd", "en"),
      end_date: format_date(new Date(nextMonth.setDate(nextMonth.getDate() - 1)), "yyyy-mm-dd", "en"),
      ...extraState,
    });
  };

  openDepositModal = (item) => {
    if (!perm.update) return;
    this.setState({ modal_update: true, bill_group_id: item.bill_group_id, rw_number: item.rw_number });
  };

  render() {
    const { data, data_day, start_date, end_date, select_month, filter_type, modal_update, modal_report, modal_report2, rw_number, deposit_date } = this.state;

    return (
      <div className="w-100 min-vh-100 fade-in">
        <Navbar page1="หน้าหลัก" page2="ข้อมูลจัดเก็บค่าใช้จ่าย" page3="การนำส่งเงินค่าใช้จ่าย" />
        <div className="body">
          <div className="p-3">
            <div className="card border-0 p-3">
              {/* หัวข้อ + ปุ่มรายงาน */}
              <div className="d-flex row mx-0 align-items-center justify-content-between">
                <h4 className="mb-3">
                  <b>การนำส่งเงินค่าใช้จ่าย</b>
                </h4>
                {perm.view && (
                  <div className="d-flex row mx-0">
                    <button className="btn btn-outline-primary mx-1 mb-3" onClick={() => report_remittance_1(data_day || [], start_date || new Date())}>
                      พิมพ์รายงานจัดเก็บประจำวัน
                    </button>
                    <button
                      className="btn btn-outline-primary mx-1 mb-3"
                      onClick={async () => {
                        await this.GetNopageAll();
                        this.GetById2();
                      }}
                    >
                      พิมพ์รายงานจัดเก็บประจำเดือน
                    </button>
                    <button className="btn btn-outline-primary mx-1 mb-3" onClick={() => this.setState({ modal_report: true, filter_type: "1" })}>
                      พิมพ์รายงานรับเงินประจำเดือน
                    </button>
                    <button className="btn btn-outline-primary mx-1 mb-3" onClick={() => this.setState({ modal_report2: true, filter_type: "2" })}>
                      พิมพ์รายงานเงินโอนนำส่งแล้ว
                    </button>
                  </div>
                )}
              </div>

              {/* ตัวกรอง */}
              {perm.view && (
                <div className="d-flex justify-content-between">
                  <div className="d-flex row mx-0">
                    <input type="search" className="form-control wpx-200 bg-contain border-0 mx-1 mb-3" placeholder="พิมพ์คำค้นหา..." onChange={(e) => this.setState({ filter_data_search: e.target.value })} />
                    <input type="month" className="form-control wpx-200 bg-contain border-0 mx-1 mb-3" value={select_month} onChange={(e) => this.onSelectMonth(e.target.value, { select_month: e.target.value })} />
                    <button
                      className="btn btn-primary px-3 mb-3 mx-1"
                      onClick={() =>
                        this.setState({ page: 1 }, () => {
                          this.GetAll();
                          this.GetReportDay();
                        })
                      }
                    >
                      ค้นหา
                    </button>
                    <button
                      className="btn btn-outline-primary px-3 mb-3 mx-1"
                      onClick={() =>
                        this.setState({ filter_data_search: "", start_date: "", end_date: "", select_month: "", page: 1 }, () => {
                          this.GetAll();
                          this.GetReportDay();
                        })
                      }
                    >
                      รีเซ็ท
                    </button>
                  </div>
                </div>
              )}

              {/* ตารางเอกสารนำส่ง */}
              <div className="table-responsive mb-3">
                <table className="table table-borderless table-striped">
                  <thead>
                    <tr className="bg-dark-blue text-white">
                      {TABLE_HEADERS.map((h, i) => (
                        <th key={i} className="text-nowrap text-left">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {!data?.data?.length ? (
                      <tr>
                        <td colSpan={20} className="text-center">
                          {" "}
                          ไม่มีข้อมูล{" "}
                        </td>
                      </tr>
                    ) : (
                      data.data.map((item, index) => (
                        <tr key={index}>
                          <td>{item.rw_number || "-"}</td>
                          <td>{item.close_job_date ? format_date(item.close_job_date, "dd/mm/yy") : "-"}</td>
                          <td className="text-nowrap">{PAYMENT_LABELS[item.payment_type] || "-"}</td>
                          <td>{toFixed(depositTotal(item))}</td>

                          {/* วันที่นำฝาก — ยังไม่บันทึกและเกิน 2 วันแล้วจะขึ้น "ผิดปกติ" สีแดง */}
                          <td className="text-center">
                            {item.deposit_date ? (
                              format_date(item.deposit_date, "dd/mm/yy")
                            ) : new Date() - new Date(item.close_job_date) > DEPOSIT_OVERDUE_MS ? (
                              <label className="text-danger pointer" data-toggle="tooltip" title="ผิดปกติ ยังไม่บันทึกการนำฝากเงิน" onClick={() => this.openDepositModal(item)}>
                                ผิดปกติ
                              </label>
                            ) : (
                              <label className="text-warning pointer" onClick={() => this.openDepositModal(item)}>
                                ไม่ได้บันทึก
                              </label>
                            )}
                          </td>

                          <td>{item.first_iw_number || "-"}</td>
                          <td>{item.last_iw_number || "-"}</td>
                          <td>{toFixed(item.water_bill || 0)}</td>
                          <td>{toFixed(item.equipment_maintenance_fee || 0)}</td>
                          <td>{toFixed(item.common_expenses || 0)}</td>
                          <td>{toFixed(item.waste_cost || 0)}</td>
                          <td>{toFixed(item.cable_cost || 0)}</td>
                          <td>{toFixed(item.other_income || 0)}</td>
                          <td>{toFixed(item.fire_cost || 0)}</td>
                          {/* add_debt/reduce_debt รวม add_on_* มาแล้วจาก processBillData — บวกซ้ำจะได้ 2 เท่า */}
                          <td>{toFixed((item.add_debt || 0) - (item.reduce_debt || 0))}</td>
                          <td>{toFixed(item.meter_connect_cost || 0)}</td>
                          <td>{toFixed(item.late_payment || 0)}</td>
                          <td>{toFixed(item.change_satang || 0)}</td>
                          <td>{toFixed(depositTotal(item))}</td>

                          <td className="text-center">
                            <ActionIcon color="primary" icon={ICON.print} title="พิมพ์รายงาน" className="mr-2" onClick={() => this.setState({ array_rw_number: [item.rw_number] }, () => this.GetById("by_id", item))} />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <Paginate data={data} page={this.state.page} onChange={(value) => this.setState({ page: value }, this.GetAll)} />

              {/* ตารางซ่อน — เป็นแหล่งข้อมูลของการส่งออก Excel เท่านั้น */}
              <table ref={this.tableRef} className="d-none">
                <thead>
                  <tr>
                    {EXCEL_HEADERS.map((h) => (
                      <td key={h}>{h}</td>
                    ))}
                  </tr>
                </thead>
                <tbody>{this.state.data_table}</tbody>
              </table>
            </div>
          </div>
        </div>

        {/* popup บันทึกวันที่นำเงินฝากธนาคาร */}
        <Modal show={modal_update} onHide={() => this.setState({ modal_update: false })}>
          <Modal.Header>
            <Modal.Title>
              <b>ลงข้อมูลวันที่นำเงินฝากธนาคาร</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="w-100 d-flex align-items-center mb-3">
              <label className="wpx-140">เลขที่ใบเสร็จรับเงิน:</label>
              <input className="form-control border-0 bg-contain" type="text" value={rw_number} readOnly />
            </div>
            <div className="w-100 d-flex align-items-center mb-3">
              <label className="wpx-140">วันที่นำเงินฝากธนาคาร:</label>
              <DatePicker className="form-control bg-contain border-0" onChange={(e) => this.setState({ deposit_date: e })} value={deposit_date} />
            </div>
            <div className="w-100 d-flex">
              <div className="w-50 p-1">
                <button className="btn btn-outline-primary w-100" onClick={() => this.setState({ modal_update: false })}>
                  ยกเลิก
                </button>
              </div>
              <div className="w-50 p-1">
                <button className="btn btn-primary w-100" onClick={this.Update}>
                  ยืนยัน
                </button>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* popup รายงานรับเงินประจำเดือน (เลือกประเภทการชำระ + ช่วงวันที่) */}
        <Modal show={modal_report} onHide={() => this.setState({ modal_report: false })}>
          <Modal.Header>
            <Modal.Title>
              <b>รายงานเงินประจำเดือน</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="w-100 d-flex align-items-center mb-3">
              <label className="wpx-140">ประเภทการชำระ:</label>
              <select className="form-control bg-contain border-0" onChange={(e) => this.setState({ filter_type: e.target.value })} value={filter_type}>
                <option value="1">เงินสด</option>
                <option value="2">เงินโอน</option>
              </select>
            </div>
            <div className="w-100 d-flex align-items-center mb-3">
              <label className="wpx-140">วันที่เริ่มต้น:</label>
              <DatePicker className="form-control bg-contain border-0" onChange={(e) => this.setState({ start_date: e })} value={start_date} />
            </div>
            <div className="w-100 d-flex align-items-center mb-3">
              <label className="wpx-140">วันที่สิ้นสุด:</label>
              <DatePicker className="form-control bg-contain border-0" onChange={(e) => this.setState({ end_date: e })} value={end_date} />
            </div>
            <div className="w-100 d-flex">
              <div className="w-100 p-1">
                <button className="btn btn-outline-primary w-100" onClick={() => this.setState({ modal_report: false })}>
                  ยกเลิก
                </button>
              </div>
              <div className="w-100 p-1">
                <button className="btn btn-primary w-100" onClick={() => this.ExportReport("pdf")}>
                  PDF
                </button>
              </div>
              <div className="w-100 p-1">
                <button className="btn btn-info w-100" onClick={() => this.ExportReport("excel")}>
                  EXCEL
                </button>
                <DownloadTableExcel
                  filename={`รายงาน${filter_type === "1" ? "เงินสด" : "เงินโอน"}ที่ได้รับแจ้ง`}
                  sheet={`รายงาน${filter_type === "1" ? "เงินสด" : "เงินโอน"}ที่ได้รับแจ้ง`}
                  currentTableRef={this.tableRef.current}
                >
                  <button className="d-none" id="export_excel"></button>
                </DownloadTableExcel>
              </div>
            </div>
          </Modal.Body>
        </Modal>

        {/* popup รายงานเงินโอนนำส่งแล้ว (เลือกเดือน) */}
        <Modal show={modal_report2} onHide={() => this.setState({ modal_report2: false })}>
          <Modal.Header>
            <Modal.Title>
              <b>รายงานเงินประจำเดือน</b>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="w-100 d-flex align-items-center mb-3">
              <label className="wpx-140">เดือนที่:</label>
              <input type="month" className="form-control bg-contain border-0" onChange={(e) => this.onSelectMonth(e.target.value)} />
            </div>
            <div className="w-100 d-flex">
              <div className="w-100 p-1">
                <button className="btn btn-outline-primary w-100" onClick={() => this.setState({ modal_report2: false })}>
                  ยกเลิก
                </button>
              </div>
              <div className="w-100 p-1">
                <button className="btn btn-primary w-100" onClick={() => this.ExportReport("pdf2")}>
                  PDF
                </button>
              </div>
              <div className="w-100 p-1">
                <button className="btn btn-info w-100" onClick={() => this.ExportReport("excel2")}>
                  EXCEL
                </button>
                <DownloadTableExcel filename="รายงานเงินโอนนำส่งแล้ว" sheet="รายงานเงินโอนนำส่งแล้ว" currentTableRef={this.tableRef.current}>
                  <button className="d-none" id="export_excel"></button>
                </DownloadTableExcel>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    );
  }
}
